import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, X, ArrowRight, CheckCircle2, AlertCircle, Sparkles, BellRing, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { getLocalTxCodes, removeLocalTx } from "@/lib/localHistory";
import { STATUS_LABEL } from "@/lib/constants";
import { playChime, pushSupported, subscribeClientPush, syncClientPush, CLIENT_PUSH_KEY } from "@/lib/push";
import { toast } from "sonner";

const READ_KEY = "sl_notif_read"; // map code → last seen status

const readJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || "{}"); } catch (_e) { return {}; } };
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_e) { /* ignore */ } };

/**
 * Notification bell — surfaces transaction status updates across the user's local history.
 * Polls every 60s when the dropdown is open or the page loads.
 */
export default function NotificationBell() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]); // [{ code, status, prev, at, read }]
  const [serverNotifs, setServerNotifs] = useState([]); // [{id,title,body,url,read,created_at}]
  const [serverUnread, setServerUnread] = useState(0);
  const [pushOn, setPushOn] = useState(
    () => pushSupported() && Notification.permission === "granted" && localStorage.getItem(CLIENT_PUSH_KEY) === "1"
  );
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef(null);
  const chimed = useRef({}); // code → status already chimed this session

  const hide = pathname.startsWith("/admin") || pathname.startsWith("/agent/");

  const refresh = useCallback(async () => {
    if (hide) return;
    // Server-side notifications (missions, partner commissions) for logged-in users.
    if (localStorage.getItem("sl_token")) {
      try {
        const { data } = await api.get("/me/notifications");
        setServerNotifs(data?.items || []);
        setServerUnread(data?.unread || 0);
      } catch (_e) { setServerNotifs([]); setServerUnread(0); }
    } else {
      setServerNotifs([]); setServerUnread(0);
    }
    const codes = getLocalTxCodes();
    if (!codes.length) { setNotifs([]); return; }
    const read = readJSON(READ_KEY);
    const items = [];
    for (const code of codes.slice(0, 8)) {
      try {
        const { data } = await api.get(`/transactions/track/${code}`);
        if (!data) continue;
        items.push({
          code,
          status: data.status,
          prev: read[code] || null,
          at: data.updated_at || data.created_at,
          read: read[code] === data.status,
        });
      } catch (_e) { /* skip bad code */ }
    }
    setNotifs(items);
    // Sound alert: ring once per new status change (only when a previous status was known)
    for (const n of items) {
      if (n.prev && n.prev !== n.status && chimed.current[n.code] !== n.status) {
        chimed.current[n.code] = n.status;
        playChime();
        toast.info(`🔔 ${n.code} : ${STATUS_LABEL[n.status] || n.status}`);
      }
    }
  }, [hide]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (hide) return;
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh, hide]);

  // Re-sync client push subscription (new tx codes, SW re-registered after deploy)
  useEffect(() => {
    if (hide) return;
    syncClientPush();
  }, [hide]);

  const enablePush = async () => {
    if (!pushSupported()) {
      toast.error("Notifications non supportées par ce navigateur.");
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permission refusée. Activez les notifications dans les réglages du navigateur.");
        return;
      }
      await subscribeClientPush();
      setPushOn(true);
      toast.success("Vous serez notifié à chaque changement de statut de vos transferts ✓");
    } catch (_e) {
      toast.error("Impossible d'activer les notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = notifs.filter((n) => !n.read).length + serverUnread;

  const markAllRead = () => {
    const read = readJSON(READ_KEY);
    notifs.forEach((n) => { read[n.code] = n.status; });
    writeJSON(READ_KEY, read);
    setNotifs((p) => p.map((n) => ({ ...n, read: true })));
    if (localStorage.getItem("sl_token") && serverUnread > 0) {
      api.post("/me/notifications/read").catch(() => {});
      setServerNotifs((p) => p.map((n) => ({ ...n, read: true })));
      setServerUnread(0);
    }
  };

  const deleteServerNotif = (id) => {
    api.delete(`/me/notifications/${id}`).catch(() => {});
    setServerNotifs((p) => p.filter((n) => n.id !== id));
    setServerUnread((u) => Math.max(0, u - (serverNotifs.find((n) => n.id === id)?.read ? 0 : 1)));
  };

  const deleteLocalNotif = (code) => {
    removeLocalTx(code);
    setNotifs((p) => p.filter((n) => n.code !== code));
  };

  const deleteAll = () => {
    if (serverNotifs.length) api.delete("/me/notifications").catch(() => {});
    notifs.forEach((n) => removeLocalTx(n.code));
    setServerNotifs([]);
    setServerUnread(0);
    setNotifs([]);
  };

  if (hide) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="relative w-9 h-9 rounded-full grid place-items-center text-gray-700 hover:bg-gray-100 transition"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center px-1"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 max-w-[92vw] bg-white rounded-2xl border border-gray-200 shadow-[0_18px_45px_-12px_rgba(0,0,0,0.18)] overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="font-heading font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-[var(--sl-primary)] hover:underline" data-testid="notif-mark-all-read">
                  Tout marquer lu
                </button>
              )}
              {(notifs.length > 0 || serverNotifs.length > 0) && (
                <button onClick={deleteAll} className="text-xs text-gray-400 hover:text-red-500 hover:underline" data-testid="notif-delete-all">
                  Tout supprimer
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={14} /></button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!pushOn && pushSupported() && (
              <button
                type="button"
                onClick={enablePush}
                disabled={pushBusy}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-[var(--sl-primary)] bg-[var(--sl-primary)]/5 hover:bg-[var(--sl-primary)]/10 border-b border-gray-100 transition"
                data-testid="client-enable-push-btn"
              >
                <BellRing size={13} />
                {pushBusy ? "Activation…" : "Activer les notifications push (statuts de vos transferts)"}
              </button>
            )}
            {notifs.length === 0 && serverNotifs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                <Sparkles size={20} className="mx-auto text-gray-300 mb-2" />
                Aucune notification pour le moment.
              </div>
            ) : (
              <>
                {serverNotifs.map((n) => {
                  const tint = n.type === "partner" ? "text-amber-600 bg-amber-50"
                    : n.type === "verification" ? "text-emerald-600 bg-emerald-50"
                    : n.type === "referral_payout" ? "text-sky-600 bg-sky-50"
                    : (n.type === "league_reward" || n.type === "league_alert") ? "text-amber-700 bg-amber-50"
                    : "text-[var(--sl-primary)] bg-[var(--sl-primary)]/10";
                  const NotifIcon = n.type === "verification" ? CheckCircle2 : Sparkles;
                  const Inner = (
                    <>
                      <span className={`w-8 h-8 rounded-full grid place-items-center flex-shrink-0 ${tint}`}>
                        <NotifIcon size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-gray-900 truncate">{n.title}</span>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5 leading-snug">{n.body}</div>
                      </div>
                    </>
                  );
                  const cls = `flex items-center gap-1.5 px-4 py-3 hover:bg-gray-50 transition border-b border-gray-100 ${!n.read ? "bg-blue-50/30" : ""}`;
                  const content = n.url ? (
                    <Link to={n.url} onClick={() => setOpen(false)} className="flex items-start gap-3 flex-1 min-w-0">{Inner}</Link>
                  ) : (
                    <div className="flex items-start gap-3 flex-1 min-w-0">{Inner}</div>
                  );
                  return (
                    <div key={n.id} className={cls} data-testid={`notif-server-${n.id}`}>
                      {content}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteServerNotif(n.id); }}
                        className="text-gray-300 hover:text-red-500 flex-shrink-0 p-1"
                        aria-label="Supprimer"
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
                {notifs.map((n) => {
                  const isCancel = n.status === "annule";
                  const isDone = n.status === "termine";
                  const Icon = isCancel ? AlertCircle : isDone ? CheckCircle2 : Sparkles;
                  const tint = isCancel ? "text-red-600 bg-red-50" : isDone ? "text-emerald-600 bg-emerald-50" : "text-[var(--sl-primary)] bg-[var(--sl-primary)]/10";
                  return (
                    <div
                      key={n.code}
                      className={`flex items-center gap-1.5 px-4 py-3 hover:bg-gray-50 transition border-b border-gray-100 last:border-0 ${!n.read ? "bg-blue-50/30" : ""}`}
                      data-testid={`notif-item-${n.code}`}
                    >
                      <Link to={`/suivi?code=${n.code}`} onClick={() => setOpen(false)} className="flex items-start gap-3 flex-1 min-w-0">
                        <span className={`w-8 h-8 rounded-full grid place-items-center flex-shrink-0 ${tint}`}>
                          <Icon size={14} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-bold text-gray-900">{n.code}</span>
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">{STATUS_LABEL[n.status] || n.status}</div>
                        </div>
                        <ArrowRight size={12} className="text-gray-300 mt-1.5" />
                      </Link>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteLocalNotif(n.code); }}
                        className="text-gray-300 hover:text-red-500 flex-shrink-0 p-1"
                        aria-label="Supprimer"
                        data-testid={`notif-delete-local-${n.code}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
